import { marked } from 'marked';

export const getCookie = (name: string) => {
  const cookieArr = document.cookie.split(';');
  for (let i = 0; i < cookieArr.length; i++) {
    let cookie = cookieArr[i].trim();
    if (cookie.startsWith(name + '=')) {
      return cookie.substring(name.length + 1);
    }
  }
  return null;
};

export type UserEligibilityType = {
  membershipId: string;
  insuranceCompanyName: string;
  primaryInsuranceSubscriber: boolean;
  dateOfBirth: string;
};

export type SignUpFormType = {
  firstName: string;
  lastName: string;
  userName: string;
  email: string;
  password: string;
  confirmPassword: string;
  phoneNumber: string;
  dateOfBirth?: string;
  guarantorNumber?: string;
  guarantorLastName?: string;
  uniqueCustomerIdentifier?: string;
};

// Retry mechanism utility function
export interface RetryConfig {
  maxRetries?: number;
  delayMs?: number;
  backoffMultiplier?: number;
}

export const withRetry = async <T>(
  fn: () => Promise<T>,
  config: RetryConfig = {}
): Promise<T> => {
  const {
    maxRetries = 3,
    delayMs = 1000,
    backoffMultiplier = 2
  } = config;

  let lastError: any;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      
      if (attempt === maxRetries) {
        // This was the last attempt, throw the error
        throw error;
      }

      // Calculate delay with exponential backoff
      const delay = delayMs * Math.pow(backoffMultiplier, attempt);
      console.warn(`API call failed (attempt ${attempt + 1}/${maxRetries + 1}), retrying in ${delay}ms:`, error);
      
      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  // This should never be reached, but TypeScript requires it
  throw lastError;
};


export const getTimeDifference = (date: Date | number): boolean => {
  const inputTime = typeof date === 'number' ? date : date.getTime();
  const differenceInMilliseconds = Math.abs(inputTime - Date.now());
  return differenceInMilliseconds >= 60000;
};

export const debounce = (func: Function, delay: number) => {
  let timer: any;
  // @ts-ignore
  return function (...args) {
    clearTimeout(timer);
    timer = setTimeout(() => {
      // @ts-ignore
      func.apply(this, args);
    }, delay);
  };
};

export const goBack = () => {
  if (window.history.length > 1) {
    window.history.back();
  } else {
    window.location.href = '/';
  }
};

// Phone number validation utility
export const validatePhoneNumber = (phoneNumber: string): boolean => {
  const digits = phoneNumber.replace(/\D/g, '');
  return digits.length === 10;
};

export const formatPhoneNumber = (phoneNumber: string): string => {
  // Remove all non-digits
  const digits = phoneNumber.replace(/\D/g, '');
  
  // Limit to 10 digits
  const limitedDigits = digits.slice(0, 10);
  
  // Format as XXX-XXX-XXXX
  if (limitedDigits.length <= 3) {
    return limitedDigits;
  } else if (limitedDigits.length <= 6) {
    return `${limitedDigits.slice(0, 3)}-${limitedDigits.slice(3)}`;
  } else {
    return `${limitedDigits.slice(0, 3)}-${limitedDigits.slice(3, 6)}-${limitedDigits.slice(6)}`;
  }
};

export const getPhoneNumberForBackend = (phoneNumber: string): string => {
  const digits = phoneNumber.replace(/\D/g, '');
  return `+1${digits}`;
};

// Address validation utilities
export const validateZipCode = (zipCode: string): boolean => {
  // Accepts 5 digits or 5+4 format (e.g., 12345 or 12345-6789)
  return /^\d{5}(-\d{4})?$/.test(zipCode.trim());
};

export const validateStateAbbreviation = (state: string): boolean => {
  // Must be exactly 2 characters
  return state.trim().length === 2;
};

export const formatZipCode = (zipCode: string): string => {
  // Remove all non-digits and hyphens
  const cleaned = zipCode.replace(/[^\d-]/g, '');
  
  // If it's 9 digits, add hyphen after 5th digit
  if (cleaned.replace(/\D/g, '').length === 9) {
    const digits = cleaned.replace(/\D/g, '');
    return `${digits.slice(0, 5)}-${digits.slice(5)}`;
  }
  
  return cleaned;
};

// Complete address validation utility
export const validateCompleteAddress = (
  streetAddress: string,
  city: string,
  state: string,
  zipCode: string
): boolean => {
  return (
    streetAddress.trim() !== '' &&
    city.trim() !== '' &&
    state.trim() !== '' &&
    validateZipCode(zipCode)
  );
};

// Format complete address for display or backend
export const formatCompleteAddress = (
  streetAddress: string,
  city: string,
  state: string,
  zipCode: string
): string => {
  return `${streetAddress.trim()}, ${city.trim()}, ${state.trim()} ${zipCode.trim()}`;
};

export function throttle(func: Function, limit: number) {
  let lastFunc: NodeJS.Timeout;
  let lastRan: number;

  return function (this: any, ...args: any[]) {
    const context = this;
    const now = Date.now();

    if (!lastRan) {
      func.apply(context, args);
      lastRan = now;
    } else {
      clearTimeout(lastFunc);
      lastFunc = setTimeout(
        () => {
          if (now - lastRan >= limit) {
            func.apply(context, args);
            lastRan = now;
          }
        },
        limit - (now - lastRan)
      );
    }
  };
}

export const logout = async () => {
  // Clear all cookies by setting them to expire in the past
  const cookies = document.cookie.split(';');
  cookies.forEach(cookie => {
    const eqPos = cookie.indexOf('=');
    const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
    // Clear cookie with various path and domain combinations to ensure complete removal
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${window.location.hostname}`;
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${window.location.hostname}`;
  });
  
  await localStorage.setItem('isAuthenticated', 'false');
  await localStorage.setItem('idToken', '');
  await localStorage.setItem('username', '');
  await localStorage.setItem('firstName', '');
  await localStorage.setItem('lastName', '');
  await localStorage.setItem('email', '');
  await localStorage.setItem('phone', '');
  await localStorage.setItem('userid', '');
  await localStorage.setItem('authTimestamp', new Date().getTime() + '');
  if (isOvum()) {
    window.close();
    window.location.href = 'https://ovumhealth.com/';
  } else {
    window.location.href = '/';
  }
};

export const setCookie = (
  value: string,
  name = 'Authorized',
  days = 1,
  secure = false,
  sameSite = 'Lax'
) => {
  let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)};`;
  const date = new Date();
  date.setDate(date.getDate() + days);
  cookieString += `expires=${date.toUTCString()};`;
  if (secure) {
    cookieString += 'Secure;';
  }
  cookieString += `SameSite=${sameSite};`;
  cookieString += 'path=/;';
  document.cookie = cookieString;
};

export const isOvum = () => {
  return window.location.href.includes('ovum');
}

export const isStageOrDevEnv = () => {
  return (window.location.href.includes('stage') || window.location.href.includes('localhost') || window.location.href.includes('dev'));
}

export const getPayabliSrc = () => {
  if(isStageOrDevEnv()) {
    return "https://embedded-component-sandbox.payabli.com/component.js"
  } else {
    return "https://embedded-component.payabli.com/component.js"
  }
}

export const formatDateToYYYYMMDD = (date: any) => {
  const [year, month, day] = date.split('-');
  return `${year}-${month}-${day}`;
};

export function convertDateFormat(dateString: string) {
  try {
    const date = new Date(dateString);
    const options: any = { year: 'numeric', month: '2-digit', day: '2-digit' };
    const formattedDate = new Intl.DateTimeFormat('en-US', options).format(date);
    return formattedDate;
  } catch (err: any){
    return dateString;
  }
}


export const localStorageUpdateAfterLogin = (response: any) => {
  if (!response) return;

  const { data } = response;
  const { user } = data;
  const currentTime = Date.now();

  setCookie(data.accessToken);
  localStorage.setItem("isAuthenticated", "true");
  localStorage.setItem("idToken", data.idToken);
  localStorage.setItem("authTimestamp", currentTime.toString());

  if (user) {
    localStorage.setItem("username", user.username);
    localStorage.setItem("userid", user.id);
    localStorage.setItem("firstName", user.firstName);
    localStorage.setItem("lastName", user.lastName);
    localStorage.setItem("email", user.email);
    localStorage.setItem("phone", user.phone);
  }
};


// Function to calculate a score based on word matches
const getMatchScore = (option: string, searchTerms: string[]): number => {
  const lowerOption = option.toLowerCase();
  let score = 0;

  searchTerms.forEach(term => {
    if (lowerOption.includes(term)) {
      score += 1; // Increase score for each term found
      if (lowerOption.startsWith(term)) {
        score += 1; // Extra point if the term is a prefix
      }
    }
  });

  return score;
};

// Enhanced search function to support multi-word search terms
export const smartSearch = (options: string[], searchTerm: string): string[] => {
  const searchTerms = searchTerm.toLowerCase().split(/\s+/); // Split search terms by spaces

  return options
    .map(option => ({ 
      option, 
      score: getMatchScore(option, searchTerms) 
    }))
    .filter(({ score }) => score > 0) // Keep only matched options
    .sort((a, b) => b.score - a.score) // Sort by score, highest first
    .map(({ option }) => option); // Return only the options
};

// Fixed keyword replacement map
export const KEYWORD_REPLACEMENTS: Record<string, string> = {
  '[link provided]': 'https://api.leadconnectorhq.com/widget/booking/ZUETBgjgi2AShUviZMlD',
};

// Function to preprocess markdown text with keyword replacements before marked processes it
export const preprocessMarkdownText = (text: string): string => {
  let processedText = text;
  
  // Pre-process text to handle keyword replacements while preserving markdown
  // This needs to be done before marked() processes the text
  Object.entries(KEYWORD_REPLACEMENTS).forEach(([keyword, replacement]) => {
    // Escape special regex characters in the keyword
    const escapedKeyword = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    // Create case-insensitive regex for the keyword
    const regex = new RegExp(escapedKeyword, 'gi');
    
    // For URLs, we can let marked handle the conversion to links
    processedText = processedText.replace(regex, replacement);
  });
  
  return processedText;
};

// Function to replace keywords in text with predefined values
export const replaceKeywords = (text: string, replacementMap: Record<string, string> = KEYWORD_REPLACEMENTS): string => {
  let processedText = text;
  // Then do the general replacements for any other keywords
  Object.entries(replacementMap).forEach(([keyword, replacement]) => {
    // Escape special regex characters in the keyword
    const escapedKeyword = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    // Create case-insensitive regex for the keyword
    const regex = new RegExp(escapedKeyword, 'gi');
    processedText = processedText.replace(regex, replacement);
  });
  
  return processedText;
};

// Function to detect URLs in text and wrap them in anchor tags with custom text
export const processLinksInText = (text: string): string => {
  // First apply keyword replacement using the fixed map
  const processedText = replaceKeywords(text);
  
  // Updated regex to only match URLs that start with http or https
  const urlRegex = /https?:\/\/([a-zA-Z0-9-]+\.)+[a-zA-Z0-9]{2,}(\/[a-zA-Z0-9-._~:/?#[\]@!$&'()*+,;=]*)?/g;
  
  const finalText = processedText.replace(urlRegex, (url) => {
    // URL already has protocol prefix
    let fullUrl = url;
    let isUrlWithDot = false;
    if(fullUrl.endsWith('.')) {
      fullUrl = fullUrl.slice(0, -1);
      isUrlWithDot = true;
    }
    const linkText = 'click here';
    return `<a href="${fullUrl}" target="_blank" rel="noopener noreferrer" class="text-blue-500 underline">${linkText}</a> ${isUrlWithDot ? '.' : ''}`;
  });
  
  return finalText;
};


export const isLowerEnvironment = () => {
  if (window?.location?.hostname?.includes('localhost') || window?.location?.hostname?.includes('dev') || window?.location?.hostname?.includes('stage')) {
    return true;
  }
  return false;
}

// Helper function to convert markdown text with keyword replacements to HTML
export const markdownToHtmlWithReplacements = async (text: string): Promise<any> => {
  // First preprocess the text to handle our keyword replacements
  const processedText = processLinksInText(text);
  const preprocessedText = preprocessMarkdownText(processedText);
  try {
    // Let marked handle the conversion of preprocessed text to HTML
    // We import the marked function here to avoid circular dependencies
    const res = await marked(preprocessedText || '');
    return res;
  } catch (error) {
    console.error('Error converting markdown to HTML:', error);
    // If marked fails, return the preprocessed text
    return preprocessedText;
  }
};

export const isAuthenticatedViaCookie = (): boolean => {
  const isAuthenticated = localStorage.getItem('isAuthenticated');
  if(isAuthenticated === 'true') {
    return true;
  }
  return false;
};

// Utility function to get user info from local storage for sidebar display
export const getUserInfoFromLocalStorage = () => {
  const firstName = localStorage.getItem('firstName') || '';
  const lastName = localStorage.getItem('lastName') || '';
  
  if (firstName && lastName) {
    return {
      name: `${firstName} ${lastName}`,
      gn: '', // Don't show guarantor number
      email: localStorage.getItem('email') || undefined,
      phone: localStorage.getItem('phone') || undefined,
      guarantorLastName: undefined // Don't show guarantor last name
    };
  }
  
  // Fallback to basic user info if local storage data is not available
  return {
    name: 'User',
    gn: 'N/A',
    email: undefined,
    phone: undefined,
    guarantorLastName: undefined
  };
};